/**
 * 侧边栏：品牌区 + 按业务分组的菜单（只显示有权限的页面）
 */
import { useMemo } from 'react'
import { Menu, type MenuProps } from 'antd'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { useAuthStore } from '@/stores/auth'
import { buildMenu, findMenuTrail, type MenuItem } from '@/router/menu'
import { getMenuSources } from '@/router/routes'
import styles from './Sidebar.module.css'

type AntdMenuItem = Required<MenuProps>['items'][number]

function toAntdItems(items: MenuItem[]): AntdMenuItem[] {
  return items.map((item) => ({
    key: item.key,
    label: item.title,
    icon: item.icon ? <item.icon /> : undefined,
    children: item.children ? toAntdItems(item.children) : undefined
  }))
}

export function Sidebar({ collapsed }: Readonly<{ collapsed: boolean }>) {
  const navigate = useNavigate()
  const pathname = useLocation({ select: (location) => location.pathname })
  const user = useAuthStore((state) => state.user)

  const menu = useMemo(() => (user ? buildMenu(getMenuSources(), useAuthStore.getState()) : []), [user])
  const items = useMemo(() => toAntdItems(menu), [menu])
  const trail = findMenuTrail(menu, pathname)
  const selectedKey = trail.at(-1)?.key
  const parentKeys = trail.slice(0, -1).map((item) => item.key)

  return (
    <div className={styles.sidebar}>
      <div className={collapsed ? styles.brandCollapsed : styles.brand}>
        <div className={styles.logo}>🎯</div>
        {!collapsed && (
          <div>
            <h3 className={styles.brandTitle}>AcquiLang AI</h3>
            <span className={styles.brandSubtitle}>管理中台</span>
          </div>
        )}
      </div>
      <Menu
        // 路由变化时用新的展开项重新挂载，保持当前页所在分组展开
        key={parentKeys.join('|')}
        mode="inline"
        inlineCollapsed={collapsed}
        items={items}
        selectedKeys={selectedKey ? [selectedKey] : []}
        defaultOpenKeys={collapsed ? [] : parentKeys}
        className={styles.menu}
        onClick={({ key }) => {
          // 菜单项的 key 就是页面路径，分组项不会触发 onClick
          navigate({ to: key })
        }}
      />
    </div>
  )
}
